/**
 * `localStorage` that cannot throw.
 *
 * Reading `window.localStorage` is not a plain property access in every
 * browser: it is a getter that raises `SecurityError` when storage is blocked
 * for the origin — Safari/WebKit with "Block all cookies", and any embed in a
 * sandboxed iframe without `allow-same-origin`. `setItem` has its own failure
 * (`QuotaExceededError`, which Safari's private mode raises on the first
 * write). Both are guarded here rather than at the call sites, because the
 * call sites are `useState` initialisers that run *during render*: a throw
 * there takes the tree down, and neither SPA has an error boundary, so the
 * whole page goes blank. `ThemeToggle` is mounted on the public pages too, so
 * that would take sign-in with it.
 *
 * The fallback is in-memory and module-scoped, so a preference set in a
 * storage-blocked browser still holds for the rest of the session and is
 * simply forgotten on reload. That is the right degradation: the feature that
 * depends on storage stops persisting, and nothing else notices.
 */
export type SafeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** Read once to prove the store *answers*, rather than merely that it exists. */
const PROBE_KEY = 'augrank.storage-probe'

/** Shared by every `safeStorage()` caller, so writes read back within a session. */
const memory = new Map<string, string>()

const memoryStorage: SafeStorage = {
  getItem: (key) => memory.get(key) ?? null,
  setItem: (key, value) => {
    memory.set(key, value)
  },
  removeItem: (key) => {
    memory.delete(key)
  },
}

export function safeStorage(): SafeStorage {
  let backing: Storage
  try {
    backing = window.localStorage
    // Touching it is the probe: some browsers hand back the object and only
    // raise on use, so a bare property read is not proof that it works.
    backing.getItem(PROBE_KEY)
  } catch {
    return memoryStorage
  }
  return {
    getItem: (key) => {
      let stored: string | null
      try {
        stored = backing.getItem(key)
      } catch {
        return memoryStorage.getItem(key)
      }
      // Falling through to memory on a miss, not only on a throw: a write that
      // hit the quota below landed in memory, and a read that ignored it would
      // hand back the old value the user has just changed.
      return stored ?? memoryStorage.getItem(key)
    },
    setItem: (key, value) => {
      try {
        backing.setItem(key, value)
      } catch {
        // Quota, or storage revoked mid-session. Keep the value for this
        // session rather than dropping the user's choice on the floor.
        memoryStorage.setItem(key, value)
      }
    },
    // SF-22. Added for the inbox's per-conversation drafts (OUTR-32), which are
    // the first caller here with something to *forget*: a draft that has been
    // sent must not come back, and a key per conversation ever opened would
    // grow without bound until it hit the quota that `setItem` guards.
    //
    // **Both stores, always.** A key can be in memory because a write hit the
    // quota and fell through, and in `localStorage` from an earlier session
    // that had room — removing one and not the other is what would make a sent
    // draft reappear on reload.
    removeItem: (key) => {
      try {
        backing.removeItem(key)
      } catch {
        // Nothing to do: the value is already unreachable through this store.
      }
      memoryStorage.removeItem(key)
    },
  }
}
